import { get as _get } from 'lodash-es'
import { computed, ref, unref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { get } from '@/api'
import { getModelApiBaseUrl } from '@/utils/apiPaths'

export function useRecord(
  { appName, modelName, /*modelType, */ id },
  { queryParameters = {} } = {},
) {
  const router = useRouter()
  id = ref(id)
  const record = ref({})
  const errorData = ref({})
  const isFetchingRecord = ref(false)
  const fetchUrl = computed(
    () => `${getModelApiBaseUrl(unref(appName), unref(modelName))}${id.value}/`,
  )

  function formatQParamsStr(qParams) {
    let qParamsStr
    if (Object.keys(qParams).length > 0) {
      const fetchParams = new URLSearchParams(qParams)
      qParamsStr = fetchParams.toString()
    }
    return qParamsStr
  }

  async function fetchRecordData(qParams = {}, { silent = false } = {}) {
    let queryParams = queryParameters || qParams
    let recData = {}
    let errData = {}
    // silent skips the loading flag so the detail shell doesn't blank/remount
    if (!silent) isFetchingRecord.value = true
    let url = fetchUrl.value
    const qParamsStr = formatQParamsStr(queryParams)
    if (qParamsStr) url += `?${qParamsStr}`
    try {
      const results = await get(url)
      recData = results.data.results
    } catch (error) {
      const { status, data } = error.response
      if (status === 404 || status === 403) {
        router.push({ name: 'notFound', query: { id: id.value } })
      } else {
        // const errorUrl = router.path
        const errorMsg =
          data?.message ||
          error.message ||
          `Could not fetch record data of record ${id}`
        errData = {
          // url: errorUrl,
          message: errorMsg,
        }
      }
    } finally {
      if (!silent) isFetchingRecord.value = false
    }
    return { recordData: recData, errorData: errData }
  }

  async function createReactiveObject(recordData) {
    Object.entries(recordData).forEach(([k, v]) => addField(k, v))
  }

  // silent: refresh the record in place without flipping the loading flag, so
  // the detail shell doesn't blank/remount (e.g. after linking ontology terms).
  async function loadRecord({ silent = false } = {}) {
    const { recordData: recData, errorData: errData } = await fetchRecordData(
      {},
      { silent },
    )
    errorData.value = errData
    createReactiveObject(recData)
  }
  const name = computed(() => {
    const fieldValue = record.value?.name
    const nameValue = _get(fieldValue, 'value', fieldValue)
    return _get(nameValue, 'name', nameValue)
  })
  const recordId = computed(() => {
    const fieldValue = record.value?.id
    return _get(fieldValue, 'value', fieldValue)
  })
  const recordStatusKey = computed(() =>
    'state' in record.value ? 'state' : 'status',
  )
  const recordStatus = computed(() => {
    let fieldValue = _get(record.value, recordStatusKey.value)
    return _get(fieldValue, 'value', fieldValue)
  })
  const isRecordLoaded = computed(() => !!recordId.value)
  const isDeleted = computed(() => {
    let fieldValue = record.value?.deleted
    fieldValue = _get(fieldValue, 'value', fieldValue)
    return fieldValue === true
  })
  const allowedActions = computed(() => {
    let fieldValue = record.value?.permissions || {}
    const permissions = _get(fieldValue, 'value', fieldValue)
    return permissions.allowed || []
  })
  const canView = computed(() => allowedActions.value.includes('view'))
  const canEdit = computed(() => allowedActions.value.includes('change'))
  const canDelete = computed(() => allowedActions.value.includes('delete'))

  function addField(key, recordValue) {
    record.value[key] = recordValue
  }
  function removeField(key) {
    // key is either the name of a field or the annotation ID
    delete record.value[key]
  }

  watch(id, (id) => id && loadRecord(), { immediate: true })

  return {
    isLoadingRecord: isFetchingRecord,
    fetchRecordData,
    loadRecord,
    errorData,
    //
    record,
    id: recordId,
    name,
    recordStatusKey,
    recordStatus,
    canView,
    canEdit,
    canDelete,
    isRecordLoaded,
    isDeleted,
    removeField,
  }
}
